/**
 * Protected Symmetric Key (`docs/ARCHITECTURE.md` §1).
 *
 * La `vaultKey` (256 bits aléatoires) chiffre les entrées. Elle est elle-même
 * scellée par `stretchedMasterKey` : le serveur ne stocke que cette version
 * chiffrée. Conséquence directe : changer de mot de passe maître revient à
 * re-sceller la `vaultKey` — PAS à re-chiffrer tout le coffre.
 */
import { SEAL_CONTEXT, SYMMETRIC_KEY_LENGTH, type AeadAlgorithmId } from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import { InvalidParameterError } from './errors.js';
import { SecretKey, assertSecretKey } from './secret.js';
import { wipe } from './bytes.js';

/**
 * `vaultKey` scellée, en base64. C'est la seule forme que le serveur voit.
 * Aussi appelée « protected symmetric key ».
 */
export type ProtectedSymmetricKey = SealedString;

/** Génère une `vaultKey` de 256 bits via le CSPRNG de la plateforme. */
export function generateVaultKey(): SecretKey {
  return SecretKey.random(SYMMETRIC_KEY_LENGTH, 'vaultKey');
}

export interface ProtectVaultKeyInput {
  readonly vaultKey: SecretKey;
  readonly stretchedMasterKey: SecretKey;
  readonly algorithm?: AeadAlgorithmId;
}

/**
 * Scelle la `vaultKey` avec `stretchedMasterKey` (nonce frais).
 * Le contexte AEAD interdit de présenter ce blob comme une entrée de coffre.
 */
export function protectVaultKey(input: ProtectVaultKeyInput): ProtectedSymmetricKey {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  assertSecretKey(input.stretchedMasterKey, 'stretchedMasterKey', SYMMETRIC_KEY_LENGTH);
  return sealToString(input.stretchedMasterKey, input.vaultKey.reveal(), {
    context: SEAL_CONTEXT.protectedSymmetricKey,
    algorithm: input.algorithm,
  });
}

export interface UnprotectVaultKeyInput {
  readonly protectedKey: ProtectedSymmetricKey;
  readonly stretchedMasterKey: SecretKey;
}

/**
 * Ouvre la protected symmetric key et renvoie la `vaultKey`.
 *
 * @throws {DecryptionFailedError} mot de passe maître incorrect, e-mail (sel)
 *   incorrect, paramètres KDF incorrects, ou blob altéré. Indistinguables :
 *   c'est cette erreur qui matérialise le refus « mauvais mot de passe ».
 * @throws {MalformedCiphertextError} conteneur structurellement invalide
 */
export function unprotectVaultKey(input: UnprotectVaultKeyInput): SecretKey {
  assertSecretKey(input?.stretchedMasterKey, 'stretchedMasterKey', SYMMETRIC_KEY_LENGTH);
  if (typeof input.protectedKey !== 'string' || input.protectedKey.length === 0) {
    throw new InvalidParameterError('protectedKey: protected symmetric key manquante');
  }
  const bytes = openFromString(input.stretchedMasterKey, input.protectedKey, {
    context: SEAL_CONTEXT.protectedSymmetricKey,
  });
  if (bytes.length !== SYMMETRIC_KEY_LENGTH) {
    // Authentifié mais de mauvaise taille : conteneur d'une autre génération.
    wipe(bytes);
    throw new InvalidParameterError('vaultKey dechiffree de longueur inattendue');
  }
  return SecretKey.adopt(bytes, 'vaultKey', SYMMETRIC_KEY_LENGTH);
}

export interface RewrapVaultKeyInput {
  readonly protectedKey: ProtectedSymmetricKey;
  /** `stretchedMasterKey` dérivée de l'ANCIEN mot de passe maître. */
  readonly currentStretchedMasterKey: SecretKey;
  /** `stretchedMasterKey` dérivée du NOUVEAU mot de passe maître. */
  readonly nextStretchedMasterKey: SecretKey;
  readonly algorithm?: AeadAlgorithmId;
}

/**
 * Changement de mot de passe maître : ouvre puis re-scelle la `vaultKey`
 * (nonce frais). Le contenu du coffre n'est pas touché.
 *
 * La `vaultKey` intermédiaire est effacée avant retour.
 */
export function rewrapVaultKey(input: RewrapVaultKeyInput): ProtectedSymmetricKey {
  assertSecretKey(input?.currentStretchedMasterKey, 'currentStretchedMasterKey', SYMMETRIC_KEY_LENGTH);
  assertSecretKey(input.nextStretchedMasterKey, 'nextStretchedMasterKey', SYMMETRIC_KEY_LENGTH);
  const vaultKey = unprotectVaultKey({
    protectedKey: input.protectedKey,
    stretchedMasterKey: input.currentStretchedMasterKey,
  });
  try {
    return protectVaultKey({
      vaultKey,
      stretchedMasterKey: input.nextStretchedMasterKey,
      algorithm: input.algorithm,
    });
  } finally {
    vaultKey.destroy();
  }
}
